export type VipCampaignStatus = 'unused' | 'requested' | 'credentials_sent' | 'activated';

export interface VipCodeValidationResult {
  valid: boolean;
  status?: VipCampaignStatus;
  slot?: number;
  message?: string;
  isMock: boolean;
}

export interface VipCampaignRequest {
  code: string;
  contactName: string;
  firm: string;
  email: string;
  seats: number;
  displayName: string;
  wish: string;
  privacyAccepted: boolean;
  phone?: string;
  website?: string;
}

export interface VipCampaignSubmissionResult {
  success: boolean;
  status?: VipCampaignStatus;
  message?: string;
  emailSent?: boolean;
  internalEmailSent?: boolean;
  pbSaved?: boolean;
  isMock: boolean;
}

interface VipCampaignWireResponse {
  success?: boolean;
  valid?: boolean;
  accepted?: boolean;
  status?: string;
  slot?: number;
  message?: string;
  emailSent?: boolean;
  internalEmailSent?: boolean;
  pbSaved?: boolean;
}

interface VipCampaignHttpResult {
  ok: boolean;
  status: number;
  data: VipCampaignWireResponse;
}

const VIP_CAMPAIGN_WEBHOOK =
  (import.meta.env.VITE_VIP_CAMPAIGN_WEBHOOK as string | undefined)?.trim() ||
  '/webhook/vip-campaign';
const VIP_CAMPAIGN_TIMEOUT_MS = 8_000;
const VIP_MOCK_STORAGE_PREFIX = 'arkorbit_vip_mock_status:';
/** Dev-Mock: Thomas ARK-VIP-01..10 und Mona 01-ARK-VIP-01..10 (kein Name in Code) */
const VIP_CODE_PATTERN = /^(?:01-)?ARK-VIP-(0[1-9]|10)$/;
const PUBLIC_STATUSES = new Set<VipCampaignStatus>([
  'unused',
  'requested',
  'credentials_sent',
  'activated',
]);

class VipCampaignTransportError extends Error {}

export function normalizeVipCode(raw: string): string {
  return raw.trim().toUpperCase();
}

function asPublicStatus(value: unknown): VipCampaignStatus | undefined {
  return typeof value === 'string' && PUBLIC_STATUSES.has(value as VipCampaignStatus)
    ? (value as VipCampaignStatus)
    : undefined;
}

function vipCodeSlot(code: string): number | undefined {
  const match = VIP_CODE_PATTERN.exec(code);
  if (!match) return undefined;
  return Number.parseInt(match[1], 10);
}

function devQueryParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

function forceDevMock(): boolean {
  if (!import.meta.env.DEV) return false;
  // Lokal: Mock ist Default (n8n-Proxy zeigt oft auf Docker-Host `n8n`).
  // Echtes Webhook-Testen: ?mock=0 oder VITE_VIP_CAMPAIGN_MOCK=false
  const params = devQueryParams();
  if (params.get('mock') === '0') return false;
  const envFlag = (import.meta.env.VITE_VIP_CAMPAIGN_MOCK as string | undefined)?.trim().toLowerCase();
  if (envFlag === 'false') return false;
  return true;
}

function readDevStatusOverride(): VipCampaignStatus | 'invalid' | undefined {
  if (!import.meta.env.DEV) return undefined;
  const value = devQueryParams().get('mockStatus');
  if (value === 'invalid') return value;
  return asPublicStatus(value);
}

function readStoredMockStatus(code: string): VipCampaignStatus {
  if (typeof window === 'undefined') return 'unused';
  try {
    return asPublicStatus(window.localStorage.getItem(`${VIP_MOCK_STORAGE_PREFIX}${code}`)) || 'unused';
  } catch {
    return 'unused';
  }
}

function writeStoredMockStatus(code: string, status: VipCampaignStatus): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${VIP_MOCK_STORAGE_PREFIX}${code}`, status);
  } catch {
    // Private browsing can block storage; the in-memory page state still updates.
  }
}

function validateWithDevMock(code: string): VipCodeValidationResult {
  const slot = vipCodeSlot(code);
  const override = readDevStatusOverride();
  if (!slot || override === 'invalid') {
    return { valid: false, message: 'Dieser VIP-Code ist nicht gültig.', isMock: true };
  }

  return {
    valid: true,
    status: override || readStoredMockStatus(code),
    slot,
    isMock: true,
  };
}

function submitWithDevMock(request: VipCampaignRequest): VipCampaignSubmissionResult {
  const validation = validateWithDevMock(request.code);
  if (!validation.valid) {
    return {
      success: false,
      message: validation.message || 'Dieser VIP-Code ist nicht gültig.',
      isMock: true,
    };
  }

  if (validation.status !== 'unused') {
    return {
      success: validation.status === 'requested',
      status: validation.status,
      message: 'Der VIP-Code wurde bereits verwendet.',
      isMock: true,
    };
  }

  writeStoredMockStatus(request.code, 'requested');
  return {
    success: true,
    status: 'requested',
    message: 'Dev-Mock: Anfrage lokal gespeichert; es wurden keine E-Mails versendet.',
    emailSent: false,
    internalEmailSent: false,
    pbSaved: false,
    isMock: true,
  };
}

async function postVipCampaign(payload: Record<string, unknown>): Promise<VipCampaignHttpResult> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), VIP_CAMPAIGN_TIMEOUT_MS);

  try {
    let response: Response;
    try {
      response = await fetch(VIP_CAMPAIGN_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } catch (error) {
      throw new VipCampaignTransportError(
        error instanceof Error ? error.message : 'VIP-Webhook nicht erreichbar',
      );
    }

    let data: VipCampaignWireResponse;
    try {
      data = (await response.json()) as VipCampaignWireResponse;
    } catch {
      throw new VipCampaignTransportError('VIP-Webhook lieferte keine JSON-Antwort');
    }

    if (!response.ok && (response.status === 404 || response.status >= 500)) {
      throw new VipCampaignTransportError(data.message || `VIP-Webhook HTTP ${response.status}`);
    }

    return { ok: response.ok, status: response.status, data };
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function validateVipCode(rawCode: string): Promise<VipCodeValidationResult> {
  const code = normalizeVipCode(rawCode);
  if (!code) {
    return { valid: false, message: 'Dieser VIP-Code ist nicht gültig.', isMock: false };
  }

  if (forceDevMock()) return validateWithDevMock(code);

  try {
    const result = await postVipCampaign({ action: 'validate', code });
    const status = asPublicStatus(result.data.status);
    return {
      valid: result.ok && result.data.valid === true && Boolean(status),
      status,
      slot: typeof result.data.slot === 'number' ? result.data.slot : undefined,
      message: result.data.message,
      isMock: false,
    };
  } catch (error) {
    if (import.meta.env.DEV && error instanceof VipCampaignTransportError) {
      return validateWithDevMock(code);
    }
    throw error;
  }
}

export async function submitVipCampaignRequest(
  request: VipCampaignRequest,
): Promise<VipCampaignSubmissionResult> {
  const normalizedRequest = { ...request, code: normalizeVipCode(request.code) };
  if (forceDevMock()) return submitWithDevMock(normalizedRequest);

  try {
    const result = await postVipCampaign({
      action: 'request',
      ...normalizedRequest,
      submittedAt: new Date().toISOString(),
    });
    return {
      success: result.ok && result.data.success === true && result.data.accepted !== false,
      status: asPublicStatus(result.data.status),
      message: result.data.message,
      emailSent: result.data.emailSent,
      internalEmailSent: result.data.internalEmailSent,
      pbSaved: result.data.pbSaved,
      isMock: false,
    };
  } catch (error) {
    if (import.meta.env.DEV && error instanceof VipCampaignTransportError) {
      return submitWithDevMock(normalizedRequest);
    }
    throw error;
  }
}
